/**
 * Content reader utilities — loads JSON content files and provides
 * typed exports matching the old siteContent / retreats / galleryProjects shapes.
 */

// ─── Page Content ───
import homeContent from "@/content/pages/home.json";
import retreatsPageContent from "@/content/pages/retreats-page.json";
import yourHostContent from "@/content/pages/your-host.json";
import galleryContent from "@/content/pages/gallery.json";
import contactContent from "@/content/pages/contact.json";
import notFoundContent from "@/content/pages/not-found.json";
import privacyPolicyContent from "@/content/pages/privacy-policy.json";
import footerContent from "@/content/pages/footer.json";
import comingSoonContent from "@/content/pages/coming-soon.json";
import underConstructionContent from "@/content/pages/under-construction.json";
import errorContent from "@/content/pages/error.json";
import unsubscribeContent from "@/content/pages/unsubscribe.json";

// ─── Retreats (auto-discovered via glob) ───
const retreatModules = import.meta.glob<{ default: Record<string, unknown> }>(
  "../content/retreats/*/index.json",
  { eager: true },
);

// ─── Gallery Projects ───
import cortonaProject from "@/content/gallery-projects/cortona/index.json";
import parkVictorianProject from "@/content/gallery-projects/park-victorian/index.json";
import provenceProject from "@/content/gallery-projects/provence/index.json";

// ─── Helpers ───

/** Resolve an image value — handles both relative filenames and full paths/URLs */
export function resolveImage(publicPath: string, value: string | null | undefined): string {
  if (!value) return "";
  if (value.startsWith("/") || value.startsWith("http")) return value;
  return `${publicPath}${value}`;
}

// ─── Types ───

export interface RetreatImages {
  hero: string;
}

export interface RetreatBase {
  slug: string;
  status: "upcoming" | "coming-soon";
  title: string;
  location: string;
  region: string;
  season: string;
  startDate?: string;
  endDate?: string;
  country?: string;
  cardDescription: string;
}

export interface DiscoveryImage {
  image: string;
  alt: string;
}

export interface DiscoverySection {
  scriptAccent?: string;
  heading: string;
  body: string;
  images: DiscoveryImage[];
}

export interface ReservationFieldContent {
  label: string;
  placeholder: string;
  note?: string;
}

export interface ReservationSectionContent {
  scriptAccent: string;
  heading: string;
  fields?: Record<string, ReservationFieldContent>;
  intro?: string;
  items?: string[];
  body?: string;
}

export interface ReservationContent {
  preface: {
    scriptAccent: string;
    heading: string;
    body: string;
  };
  sections: {
    yourDetails: ReservationSectionContent;
    yourStory: ReservationSectionContent;
    travelCompanions: ReservationSectionContent;
    healthActivities: ReservationSectionContent;
    acknowledgements: ReservationSectionContent;
    nextSteps: ReservationSectionContent;
    anythingElse: ReservationSectionContent;
  };
  submitText: string;
  submittingText: string;
  successScript: string;
  successBody: string;
  errorText: string;
}

export interface UpcomingRetreat extends RetreatBase {
  status: "upcoming";
  phase?: "waitlist" | "reservation";
  reservation?: ReservationContent;
  hero: { scriptAccent: string; heading: string };
  description: string;
  images: RetreatImages;
  discovery?: {
    scriptAccent?: string;
    heading?: string;
    intro?: string;
    sections: DiscoverySection[];
  };
  retreatSection?: {
    scriptAccent?: string;
    heading?: string;
  };
  environment: {
    scriptAccent: string;
    paragraphs: string[];
  };
  audience: {
    heading: string;
    intro: string;
    descriptions: string[];
    closingLine: string;
  };
  itinerary?: {
    scriptAccent: string;
    heading: string;
    timeBlocks: { time: string; description: string }[];
  };
  details: {
    scriptAccent: string;
    heading: string;
    duration?: string;
    includedCategories: {
      label: string;
      intro?: string;
      items?: {
        title: string;
        description?: string;
        note?: string;
      }[];
      mayInclude?: string[];
      closing?: string;
    }[];
    closingNote?: string;
    notIncluded: string[];
    aLaCarte?: string;
    cta?: {
      text: string;
      link: string;
    };
  };
  cta: {
    scriptAccent?: string;
    body?: string;
    waitlistButtonText: string;
    reservationButtonText: string;
    secondaryText?: string;
    secondaryLink?: string;
  };
}

export interface ComingSoonRetreat extends RetreatBase {
  status: "coming-soon";
}

export type Retreat = UpcomingRetreat | ComingSoonRetreat;

export interface GalleryImage {
  src: string;
  alt: string;
  width: number;
  height: number;
}

export interface GalleryProject {
  slug: string;
  title: string;
  location: string;
  description: string;
  coverImage: string;
  comingSoon?: boolean;
  images: GalleryImage[];
}

// ─── Exports ───

export { homeContent, retreatsPageContent, yourHostContent, galleryContent, contactContent, notFoundContent, privacyPolicyContent, footerContent, comingSoonContent, underConstructionContent, errorContent, unsubscribeContent };

export const retreats: Retreat[] = Object.entries(retreatModules)
  .map(([path, mod]) => {
    const slug = path.match(/retreats\/(.+?)\/index\.json/)?.[1] ?? "";
    return { ...mod.default, slug } as unknown as Retreat;
  })
  .sort((a, b) => a.slug.localeCompare(b.slug));

export function getRetreatBySlug(slug: string): Retreat | undefined {
  return retreats.find((r) => r.slug === slug);
}

export const galleryProjects: GalleryProject[] = [
  cortonaProject as GalleryProject,
  parkVictorianProject as GalleryProject,
  provenceProject as GalleryProject,
];

